import React from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail';
import Dashboard from './components/dashboard/Dashboard';
import TemplateManager from './components/template/TemplateManager';
import TemplateCollection from './components/template/TemplateCollection';
import TemplateMyList from './components/template/TemplateMyList';
import CreateTemplatePage from './components/template/CreateTemplatePage';
import EditTemplatePage from './components/template/EditTemplatePage';
import ContactManagement from './components/contact/ContactManagement';
import ViewProfile from './components/contact/ViewProfile';
import ChatLayout from './components/chat/ChatLayout';
import UnifiedChatLayout from './components/chat/UnifiedChatLayout';
import Layout from './components/layout/Layout';
import CampaignDashboard from './components/campaign/CampaignDashboard';
import CampaignDetails from './components/campaign/CampaignDetails';
import CampaignForm from './components/campaign/CampaignForm';
import MessageForm from './components/campaign/MessageForm';
import TestMessagePage from './components/messaging/TestMessagePage';
import InquiriesPage from './components/inquiries/InquiriesPage';
import WhatsAppFlowsPage from './components/flows/WhatsAppFlowsPage';
import BotManagement from './components/bot/BotManagement';
import Collection from './components/bot/Collection';
import MyList from './components/bot/MyList';
import AdminBotManagement from './components/bot/AdminBotManagement';
import BotBuilder from './components/bot/BotBuilder';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminCustomerSearch from './components/admin/AdminCustomerSearch';
import { BookOpen, List } from 'lucide-react';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  return (isAuthenticated || isAdmin) ? children : <Navigate to="/login" replace />;
};

// Conditional Bot Management Component
const ConditionalBotManagement = () => {
  const { isAdmin } = useAuth();
  
  if (isAdmin) {
    return <AdminBotManagement />;
  } else {
    return <BotManagement />;
  }
};

// Conditional Template Management Component
const ConditionalTemplateManager = () => {
  const { isAdmin } = useAuth();
  
  if (isAdmin) {
    return <TemplateManager />;
  } else {
    return <TemplateManager />;
  }
};

// Public Route Component (redirects to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin } = useAuth();
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  if (isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/signup" element={
        <PublicRoute>
          <Signup />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      } />
      <Route path="/reset-password/:token" element={
        <PublicRoute>
          <ResetPassword />
        </PublicRoute>
      } />
      <Route path="/verify-email/:token" element={
        <PublicRoute>
          <VerifyEmail />
        </PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/template-manager" element={
        <ProtectedRoute>
          <Layout>
            <ConditionalTemplateManager />
          </Layout>
        </ProtectedRoute>
      }>
        {/* Customer-only nested routes */}
        <Route index element={<Navigate to="/template-manager/my-list" replace />} />
        <Route path="collection" element={<TemplateCollection />} />
        <Route path="my-list" element={<TemplateMyList />} />
      </Route>
      <Route path="/templates/create" element={
        <ProtectedRoute>
          <CreateTemplatePage />
        </ProtectedRoute>
      } />
      <Route path="/templates/:templateId/edit" element={
        <ProtectedRoute>
          <EditTemplatePage />
        </ProtectedRoute>
      } />
      <Route path="/test-messages" element={
        <ProtectedRoute>
          <Layout>
            <TestMessagePage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/inquiries" element={
        <ProtectedRoute>
          <Layout>
            <InquiriesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/whatsapp-flows" element={
        <ProtectedRoute>
          <Layout>
            <WhatsAppFlowsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contacts" element={
        <ProtectedRoute>
          <Layout>
            <UnifiedChatLayout />
          </Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <ViewProfile />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Campaign Management Routes */}
      <Route path="/campaigns" element={
        <ProtectedRoute>
          <Layout>
            <CampaignDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/campaigns/new" element={
        <ProtectedRoute>
          <Layout>
            <CampaignForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/campaigns/:campaignId" element={
        <ProtectedRoute>
          <Layout>
            <CampaignDetails />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/campaigns/:campaignId/edit" element={
        <ProtectedRoute>
          <Layout>
            <CampaignForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/campaigns/:campaignId/messages/new" element={
        <ProtectedRoute>
          <Layout>
            <MessageForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/campaigns/:campaignId/messages/:messageId" element={
        <ProtectedRoute>
          <Layout>
            <MessageForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/campaigns/:campaignId/messages/:messageId/edit" element={
        <ProtectedRoute>
          <Layout>
            <MessageForm />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Bot Management Routes */}
      <Route path="/bots" element={
        <ProtectedRoute>
          <Layout>
            <ConditionalBotManagement />
          </Layout>
        </ProtectedRoute>
      }>
        {/* Customer-only nested routes */}
        <Route index element={<Navigate to="/bots/my-list" replace />} />
        <Route path="collection" element={<Collection />} />
        <Route path="my-list" element={<MyList />} />
        <Route path="create" element={<div />} />
        <Route path="edit/:botId" element={<div />} />
      </Route>
      
      {/* Flow Builder Routes - Available for both admin and customer */}
      <Route path="/bots/builder/:botId" element={
        <ProtectedRoute>
          <Layout>
            <BotBuilder />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <AdminDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/customers" element={
        <ProtectedRoute>
          <Layout>
            <AdminCustomerSearch />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
